import { IAppContext } from 'App/AppContext';
import { globalTaskDefaultDeadline, globalTaskDefaultDuration } from 'globalConstants';
import { TFunction } from 'i18next';
import Joi from 'joi';
import { ApprovalState } from 'models/approval';
import { Role } from 'models/auth/role';
import Entity, { EntityTypes } from 'models/entity';
import GlobalDataCache from 'models/globalDataCache/globalDataCache';
import KPIData from 'models/kpi/kpiData';
import Webhook from 'models/webhook';
import { getLocalizedMessageOptions } from 'services/Localization/joiValidation';
import AppError from 'utils/appError';
import { areDifferent } from 'utils/array';
import {
  addDateTimeDays,
  addDateTimeHours,
  addDateTimeMinutes,
  getDateTimeDiff,
  getDateTimeDiffDays,
  getDateTimeDiffMinute,
  setNewDateKeepTime,
  setNewTimeKeepDate,
} from 'utils/datetime';
import { newGuid, newGuidNil } from 'utils/guid';
import AuditTrail from '../auditTrail';
import RecurringPattern from '../recurringPattern';
import User from '../user';
import { TaskCheckList, TaskState } from './taskHelperClasses';
import { areTaskRelationsDifferent, TaskRelation } from './taskRelation';
import TaskType, { SystemTaskTypes, TaskKinds } from './taskType';

//
// Task
//
export enum TaskStateId {
  Requested = 5,
  Accepted = 1,
  InProgress = 2,
  Completed = 4,
  Rejected = 6,
  OnHold = 3,
  Archived = 7,
}

export enum TaskTypes {
  Normal = 0,
  Monitoring = 1,
  Template = 2,
  Event = 3,
  EventTemplate = 4,
}

//These task types represent types of workable tasks
//This excludes series and templates
export enum TaskWorkableTypes {
  None = 0,
  Recurring = 1,
  Single = 2,
  Event = 3,
  Subtask = 4,
  Approval = 5,
  LegacyRecurring = 6,
}

export enum TaskWorkflowStatus {
  NotSet = 0,
  Success = 1,
  Failure = 2,
}

export enum TaskColors {
  Normal = '#eff6fc',
  DarkNormal = '#106ebe',
  Completed = '#9bcc97',
  DarkCompleted = '#52854e',
  OverDeadline = '#fc8d81',
  DarkOverDeadline = '#a1574f',
  InProgress = '#c1b7fc',
  DarkInProgress = '#7163c0',
}

//merge entity id's without creating duplicates
const addIds = (current: number[], toAdd: number[]): number[] => {
  const output = [...current];
  for (const id of toAdd) {
    if (!output.includes(id)) {
      output.push(id);
    }
  }

  return output;
};

export default class Task {
  taskId: number;

  taskMasterId: number | undefined;

  authSchemaId?: number;

  name: string;

  description?: string;

  taskStateId: number;

  taskType: TaskTypes;

  taskTypeId?: number;

  taskTypeFamilyId?: number;

  formVersion?: TaskType;

  sortOrder: number;

  creatorId: string;

  created: Date;

  latestChangeDateTime?: Date;

  creator?: User;

  ownerId: string;

  owner?: User | Role | undefined;

  completed?: Date;

  userId?: string;

  user?: User;

  commentTrailId: number;

  // Read-only: messages in the comment trail, filled by the server for a single loaded task only; undefined = not
  // computed (the Activity count is then fetched, see useTaskActivityCount).
  commentCount?: number;

  auditTrailId: number;

  duration: number;

  startDateTime: Date;

  endDateTime: Date;

  checkList: TaskCheckList;

  eventId?: string;

  hasFollowUp: boolean;

  followUpTotal: number;

  followUpCompleted: number;

  hasUnlinkedFollowUp: boolean;

  recurringPattern: RecurringPattern;

  recurringPatternSummary?: string;

  deadline: Date;

  taskStates: TaskState[];

  controlIds: number[];

  processIds: number[];

  objectiveIds: number[];

  assetIds: number[];

  riskIds: number[];

  processingActivityIds: number[];

  normIds?: number[];

  instances?: Task[];

  instancesEnabled?: boolean;

  approved?: ApprovalState;

  hidden?: boolean;

  webhookStatus?: TaskWorkflowStatus;

  templateId?: number;

  // Read-only: the internal audit this task is the execution work of (loaded on the single-task read
  // and the audit task queries). This is how the client recognises an audit execution task - the
  // form (taskTypeId) stays free for the customer's own form.
  //
  // A finding does NOT carry this: it is not the audit's own work, it follows up on it.
  internalAuditId?: number;

  taskRelations: TaskRelation[];

  tagIds?: number[];

  resourceLinkIds?: number[];

  auditTrail?: AuditTrail;

  kpiData?: KPIData[];

  externalId?: string;

  //internal props
  id?: string;

  systemTaskType: SystemTaskTypes;

  //the server's stamp, read-only on the DTO: drives which UI SingleTask dispatches to. Cloned but
  //excluded from isEqual (same rule as systemTaskType), so a client-side stamp never marks a task dirty
  taskKind: TaskKinds;

  timelineTagKeys?: string;

  relatedEntities?: Entity[];

  webhooks?: Webhook[];

  //static props
  static completedState: number | undefined = undefined;

  constructor() {
    this.taskId = -1; //this must be -1 for new tasks (check in UI)
    this.taskMasterId = undefined;
    this.name = '';
    this.taskStateId = TaskStateId.Requested;
    this.sortOrder = 0;
    this.creatorId = '';
    this.ownerId = '';
    this.created = new Date();
    this.commentTrailId = 0; //this must be 0 for new tasks. -1 means the tenant general log
    this.auditTrailId = 0; //this must be 0 for new tasks. -1 means the tenant general log
    this.startDateTime = setNewTimeKeepDate(new Date(), 9, 0);
    this.endDateTime = addDateTimeHours(this.startDateTime, 1);
    this.duration = globalTaskDefaultDuration;
    this.deadline = addDateTimeDays(this.endDateTime, globalTaskDefaultDeadline);
    this.checkList = new TaskCheckList();
    this.recurringPattern = new RecurringPattern();
    this.hasFollowUp = false;
    this.followUpTotal = 0;
    this.followUpCompleted = 0;
    this.hasUnlinkedFollowUp = false;
    this.taskRelations = [];
    this.taskType = TaskTypes.Normal;
    this.taskStates = [];
    this.tagIds = [];
    this.controlIds = [];
    this.objectiveIds = [];
    this.processIds = [];
    this.riskIds = [];
    this.assetIds = [];
    this.processingActivityIds = [];
    this.userId = undefined;
    this.systemTaskType = SystemTaskTypes.None;
    this.taskKind = TaskKinds.None;
    this.instancesEnabled = true;
  }

  //assigning a system type (picking/removing a form) moves the system type only: the kind is fixed by the
  //server when the task is created
  setSystemTaskType(systemTaskType: SystemTaskTypes) {
    this.systemTaskType = systemTaskType;
  }

  //binding a form, or removing it (undefined), moves four fields together: the pinned version, the
  //family, the version row and the system type. The API's producer branch binds the family it is sent,
  //so setting taskTypeId alone next to the previous family left a remove or a switch reverted on save
  assignForm(taskType: TaskType | undefined) {
    //a cache miss hands back a stand-in with id 0: that is no form
    const newTaskTypeId = taskType?.taskTypeId || undefined;
    if (newTaskTypeId !== this.taskTypeId) {
      //the rows belong to the form they were answered on: another form (or none) takes none of them,
      //and the head of the task's own family re-seeds its defaults server-side. Posting them would
      //bring the old form's rows back as rows no form shows
      this.kpiData = undefined;
    }

    this.taskTypeId = newTaskTypeId;
    this.taskTypeFamilyId = taskType?.taskTypeFamilyId;
    this.formVersion = taskType?.clone();
    this.setSystemTaskType(taskType?.systemTaskType ?? SystemTaskTypes.None);
  }

  static getEmptyTaskWithStates = (states: TaskState[]) => {
    const output = new Task();
    output.taskStates = states.map((_) => _.clone());
    if (output.taskStates.length > 0) output.taskStateId = output.taskStates[0].taskStateId;

    return output;
  };

  getTypeText(t: TFunction<string[]>): string {
    switch (this.taskType) {
      case TaskTypes.Normal:
        return t('task:Types.Normal');
      case TaskTypes.Template:
        return t('task:Types.Template');
      case TaskTypes.Monitoring:
        return t('task:Types.Monitoring');
      case TaskTypes.Event:
        return t('task:Types.Event');
      case TaskTypes.EventTemplate:
        return t('task:Types.EventTemplate');
    }
  }

  getWorkableTaskType(): TaskWorkableTypes {
    if (!this.isWorkableTask()) return TaskWorkableTypes.None;

    if (this.taskType === TaskTypes.Monitoring) {
      return TaskWorkableTypes.Recurring;
    }

    if (this.taskType === TaskTypes.Normal) {
      if (this.recurringPattern?.isActive) {
        return TaskWorkableTypes.LegacyRecurring;
      } else {
        return TaskWorkableTypes.Single;
      }
    }

    if (this.taskType === TaskTypes.Event) {
      if (this.systemTaskType === SystemTaskTypes.Approval) {
        return TaskWorkableTypes.Approval;
      }

      if (this.isSeries()) {
        return TaskWorkableTypes.Event;
      } else {
        return TaskWorkableTypes.Subtask;
      }
    }

    return TaskWorkableTypes.None;
  }

  static getWorkableTypeText(workableType: TaskWorkableTypes, t: TFunction<string[]>): string {
    switch (workableType) {
      case TaskWorkableTypes.Recurring:
        return t('tasks:WorkableTypes.Recurring');
      case TaskWorkableTypes.Single:
        return t('tasks:WorkableTypes.Single');
      case TaskWorkableTypes.Event:
        return t('tasks:WorkableTypes.Event');
      case TaskWorkableTypes.Subtask:
        return t('tasks:WorkableTypes.Subtask');
      case TaskWorkableTypes.Approval:
        return t('tasks:WorkableTypes.Approval');
      case TaskWorkableTypes.LegacyRecurring:
        return t('tasks:WorkableTypes.LegacyRecurring');
      default:
        return '';
    }
  }

  //
  // Logic functions about task state
  //
  isCompleted(): boolean {
    if (this.taskStates === undefined || this.taskStates.length === 0) {
      return false;
    }

    return this.taskStates.find((t) => t.taskStateId === this.taskStateId)?.completed === true;
  }

  //Whether work on the task can still go on: the states the server treats as workable
  //(BaseTask.WorkableTaskStateIds). Completed, rejected and archived tasks are closed.
  isInWorkableState(): boolean {
    return (
      this.taskStateId === TaskStateId.Requested ||
      this.taskStateId === TaskStateId.Accepted ||
      this.taskStateId === TaskStateId.InProgress ||
      this.taskStateId === TaskStateId.OnHold
    );
  }

  isAssigned(): boolean {
    return this.userId !== undefined && this.userId !== newGuidNil();
  }

  isScheduled(): boolean {
    return this.eventId !== undefined;
  }

  isEvent(): boolean {
    return this.taskType === TaskTypes.Event;
  }

  isWorkableTask(): boolean {
    //A workable task is a task that can be 'done' by a user
    //So not a series or template, except an event series
    if (this.isSeries() && this.taskType !== TaskTypes.Event) return false;
    if (this.isTemplate()) return false;

    return true;
  }

  isSubTask(): boolean {
    return this.taskMasterId !== 0 && (this.taskType === TaskTypes.Event || this.taskType === TaskTypes.EventTemplate);
  }

  isTemplate(): boolean {
    return this.taskType === TaskTypes.EventTemplate || this.taskType === TaskTypes.Template;
  }

  //an instance of a task is a task that is an instance of a series task or template task
  //it has a taskMasterId set to the taskId of the series task
  isInstance(): boolean {
    return this.taskMasterId === undefined ? false : this.taskMasterId !== 0;
  }

  //an instance of a series with 'only allow work from the start date' is locked until its start date (browser local time, the server uses the user time zone)
  isLockedUntilStart(): boolean {
    if (!this.isInstance() || !this.recurringPattern?.preventBeforeStart || !this.startDateTime) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const start = new Date(this.startDateTime);
    start.setHours(0, 0, 0, 0);

    return start.getTime() > today.getTime();
  }

  //a series task acts like the template for new instances and controls the scheduling and assignment
  //it has the taskMasterId set to zero
  isSeries(): boolean {
    return this.taskMasterId === undefined ? false : this.taskMasterId === 0;
  }

  //the execution work of one internal audit: the server stamps internalAuditId on it (see the field
  //above). Deliberately NOT keyed off taskKind, which is InternalAudit for the procedure templates
  //as well - and on a template, editing the shared form IS legitimate.
  isAuditExecutionTask(): boolean {
    return this.internalAuditId !== undefined && this.internalAuditId > 0;
  }

  //a non recurring task is a 'normal' task that is not related to any other task
  //it has the taskMasterId set to undefined
  isNonRecurring(): boolean {
    return this.taskMasterId === undefined;
  }

  //a recurring task is a task that can be scheduled with a recurring pattern
  isRecurring(): boolean {
    //only monitoring series can be recurring
    return this.taskType === TaskTypes.Monitoring && this.taskMasterId === 0;
  }

  //
  // Deadline functions
  //
  //a normal task with an active recurring pattern has a deadline computed from its end and duration
  hasComputedDeadline(): boolean {
    return this.taskType === TaskTypes.Normal && this.recurringPattern.isActive;
  }

  getDeadline(cache: GlobalDataCache): Date {
    //for events, this should be refactored to return undefined
    return this.taskType === TaskTypes.Normal && !this.isNonRecurring()
      ? cache.settings.getStaticWorkingHours().getDeadline(this.endDateTime, this.duration)
      : this.deadline;
  }

  isOverDeadline(deadline: Date, refDate: Date): boolean {
    const minutes = getDateTimeDiffMinute(refDate, deadline);

    return minutes > 0;
  }

  getMinutesOverDeadline(refDate: Date, cache: GlobalDataCache): number {
    if (this.taskType === TaskTypes.Event && this.isSeries()) {
      return 0; //events don't have deadlines
    } else {
      const deadline: Date = this.getDeadline(cache);
      const minutes = getDateTimeDiffMinute(refDate, deadline);

      return minutes;
    }
  }

  getWorkingHoursBeforeDeadline(cache: GlobalDataCache): number {
    if (this.taskType === TaskTypes.Event && this.isSeries()) {
      return 0; //events don't have deadlines
    } else {
      const deadline: Date = this.getDeadline(cache);
      const hours = cache.settings.getStaticWorkingHours().getHoursBetween(new Date(), deadline);

      return hours;
    }
  }

  getWorkingHoursBetween(from: Date, until: Date, cache: GlobalDataCache): number {
    const hours = cache.settings.getStaticWorkingHours().getHoursBetween(from, until);

    return hours;
  }

  //
  // Instances
  //
  hasInstances(): boolean {
    return this.instances !== undefined && this.instances.length > 0;
  }

  getShowinstances(): boolean {
    return (this.instances && this.instances.length > 0) || this.instancesEnabled === true;
  }

  getScore(cache: GlobalDataCache): number {
    const completedState = this.getCompletedState();

    if (this.taskStateId !== completedState || this.completed === undefined) {
      return 0;
    }

    let score: number = 100;

    let hoursOverDeadLine = this.getMinutesOverDeadline(this.completed, cache);

    if (hoursOverDeadLine > 30) hoursOverDeadLine = 30;
    if (hoursOverDeadLine < 0) hoursOverDeadLine = 0;

    score -= (hoursOverDeadLine / 30) * 30;

    const totalItems = this.checkList.items.length;
    if (totalItems > 0) {
      const unSuccesFullItems = this.checkList.getTodoOrFailedCount();
      score -= (unSuccesFullItems / totalItems) * 30;
    }

    return score;
  }

  //
  // State functions
  //
  getCompletedState(): number {
    if (Task.completedState === undefined) {
      if (this.taskStates) {
        Task.completedState = this.taskStates.find((t) => t.completed === true)?.taskStateId;
        if (!Task.completedState) {
          return -1;
        }
      } else {
        return -1;
      }
    }

    return Task.completedState;
  }

  getFirstState(): number {
    if (!this.taskStates || this.taskStates.length === 0) return TaskStateId.Requested;

    return this.taskStates[0].taskStateId;
  }

  setComplete() {
    this.taskStateId = this.getCompletedState();
    this.completed = new Date();
  }

  setArchived() {
    this.taskStateId = TaskStateId.Archived;
  }

  //the inverse of setComplete: the task becomes work again and loses its completion stamp
  setReopened() {
    this.taskStateId = this.getFirstState();
    this.completed = undefined;
  }

  //
  // Date adjustment functions
  //
  setNewStartDate(newDate: Date) {
    //this applies a new start date to the task
    //- starttime and end times are kept
    //- startdate is applied to the recurrence pattern
    //- deadline is moved relative to the new start date (time is kept)
    const deadlineDurationDays = getDateTimeDiffDays(this.deadline, this.startDateTime);
    this.startDateTime = setNewDateKeepTime(newDate, this.startDateTime);
    this.endDateTime = setNewDateKeepTime(newDate, this.endDateTime);
    this.recurringPattern.startDate = this.startDateTime;
    const newDeadlineDate = addDateTimeDays(this.startDateTime, deadlineDurationDays);
    this.deadline = setNewDateKeepTime(newDeadlineDate, this.deadline);
  }

  setNewStartTime = (hours: number, mins: number) => {
    //calculate current durations
    const duration = getDateTimeDiffMinute(this.endDateTime, this.startDateTime);
    const deadlineDuration = getDateTimeDiffMinute(this.deadline, this.endDateTime);

    //calculate new start time based on current date
    const newDate = new Date(this.startDateTime);
    newDate.setHours(hours);
    newDate.setMinutes(mins);
    newDate.setSeconds(0);
    newDate.setMilliseconds(0);
    this.startDateTime = newDate;
    this.recurringPattern.startDate = this.startDateTime;

    //calculate new end time based on duration

    this.endDateTime = addDateTimeMinutes(this.startDateTime, duration);

    //when this is a new task, calculate a new default deadline
    if (this.taskId <= 0) {
      this.deadline = addDateTimeDays(this.startDateTime, globalTaskDefaultDeadline);
    } else {
      //for existing tasks, keep the same duration between end date and deadline
      this.deadline = addDateTimeMinutes(this.endDateTime, deadlineDuration);
    }
  };

  setNewEndTime = (hours: number, mins: number) => {
    const deadlineDuration = getDateTimeDiffMinute(this.deadline, this.endDateTime);

    //calculate new end time based on current date
    const newDate = new Date(this.startDateTime);
    newDate.setHours(hours);
    newDate.setMinutes(mins);
    newDate.setSeconds(0);
    newDate.setMilliseconds(0);
    this.endDateTime = newDate;

    //when this is a new task, calculate a new default deadline
    if (this.taskId <= 0) {
      this.deadline = addDateTimeDays(this.startDateTime, globalTaskDefaultDeadline);
    } else {
      //for existing tasks, keep the same duration between end date and deadline
      this.deadline = addDateTimeMinutes(this.endDateTime, deadlineDuration);
    }
  };

  setNewDeadline(newDeadline: Date) {
    this.deadline = setNewDateKeepTime(newDeadline, this.deadline);
  }

  setNewDeadlineTime(hours: number, mins: number) {
    this.deadline = setNewTimeKeepDate(this.deadline, hours, mins);
  }

  setNewDeadlineDuration(hours: number): void {
    this.deadline = addDateTimeHours(this.deadline, hours);
  }

  move(daysToMove: number): void {
    this.startDateTime.setDate(this.startDateTime.getDate() + daysToMove);
    this.endDateTime.setDate(this.endDateTime.getDate() + daysToMove);

    if (this.deadline) {
      this.deadline.setDate(this.deadline.getDate() + daysToMove);
    }
  }

  //
  // Helpers
  //
  getBackgroundColor(useDarkMode: boolean, cache: GlobalDataCache): string {
    let backgroundColor = useDarkMode ? TaskColors.DarkNormal : TaskColors.Normal;
    if (this.completed) {
      backgroundColor = useDarkMode ? TaskColors.DarkCompleted : TaskColors.Completed;
    } else {
      const isOverDeadline = this.completed ? false : this.getMinutesOverDeadline(new Date(), cache) > 0;
      if (isOverDeadline) {
        backgroundColor = useDarkMode ? TaskColors.DarkOverDeadline : TaskColors.OverDeadline;
      } else if (this.taskStateId === TaskStateId.InProgress) {
        //in progress
        backgroundColor = useDarkMode ? TaskColors.DarkInProgress : TaskColors.InProgress;
      }
    }

    return backgroundColor;
  }

  clone(): Task {
    const newTask: Task = new Task();

    newTask.taskId = this.taskId;
    newTask.taskStateId = this.taskStateId;
    newTask.sortOrder = this.sortOrder;
    newTask.taskMasterId = this.taskMasterId;
    newTask.taskType = this.taskType;
    newTask.taskTypeId = this.taskTypeId;
    newTask.taskTypeFamilyId = this.taskTypeFamilyId;
    newTask.formVersion = this.formVersion?.clone();
    newTask.systemTaskType = this.systemTaskType;
    newTask.taskKind = this.taskKind;
    newTask.auditTrailId = this.auditTrailId;
    newTask.commentTrailId = this.commentTrailId;
    newTask.commentCount = this.commentCount;
    newTask.checkList = this.checkList.clone();
    newTask.name = this.name;
    newTask.description = this.description;
    newTask.startDateTime = new Date(this.startDateTime);
    newTask.endDateTime = new Date(this.endDateTime);
    newTask.deadline = new Date(this.deadline);
    newTask.duration = this.duration;
    newTask.hasFollowUp = this.hasFollowUp;
    newTask.followUpTotal = this.followUpTotal;
    newTask.followUpCompleted = this.followUpCompleted;
    newTask.hasUnlinkedFollowUp = this.hasUnlinkedFollowUp;
    newTask.userId = this.userId;
    newTask.user = this.user ? this.user.clone() : undefined;
    newTask.recurringPattern = this.recurringPattern.clone();
    newTask.eventId = this.eventId;
    newTask.controlIds = [...this.controlIds];
    newTask.processIds = [...this.processIds];
    newTask.objectiveIds = [...this.objectiveIds];
    newTask.riskIds = [...this.riskIds];
    newTask.assetIds = [...this.assetIds];
    newTask.processingActivityIds = [...this.processingActivityIds];
    newTask.taskRelations = this.taskRelations.map((r) => r.clone());
    newTask.taskStates = [...this.taskStates];
    newTask.created = new Date(this.created);
    newTask.latestChangeDateTime = this.latestChangeDateTime ? new Date(this.latestChangeDateTime) : undefined;
    newTask.creatorId = this.creatorId;
    newTask.creator = this.creator ? this.creator.clone() : undefined;
    newTask.completed = this.completed ? new Date(this.completed) : undefined;
    newTask.tagIds = this.tagIds ? [...this.tagIds] : undefined;
    newTask.instances = this.instances?.map((c) => c.clone());
    newTask.instancesEnabled = this.instancesEnabled;
    newTask.hidden = this.hidden;
    newTask.approved = this.approved;
    newTask.resourceLinkIds = this.resourceLinkIds ? [...this.resourceLinkIds] : undefined;
    newTask.kpiData = this.kpiData?.map((k) => k.clone());
    newTask.templateId = this.templateId;
    newTask.internalAuditId = this.internalAuditId;
    newTask.webhookStatus = this.webhookStatus;
    newTask.id = this.id;
    newTask.authSchemaId = this.authSchemaId;
    newTask.normIds = this.normIds ? [...this.normIds] : undefined;
    newTask.relatedEntities = this.relatedEntities ? [...this.relatedEntities] : undefined;
    newTask.ownerId = this.ownerId;
    newTask.owner = this.owner ? this.owner.clone() : undefined;
    newTask.timelineTagKeys = this.timelineTagKeys;
    newTask.externalId = this.externalId;
    newTask.webhooks = this.webhooks ? [...this.webhooks] : undefined;

    return newTask;
  }

  isEqual(item: Task) {
    if (item.name !== this.name) return false;
    if (item.externalId !== this.externalId) return false;
    if (item.description !== this.description) return false;
    if (item.taskId !== this.taskId) return false;
    if (item.id !== this.id) return false;
    if (item.taskStateId !== this.taskStateId) return false;
    if (item.sortOrder !== this.sortOrder) return false;
    if (item.taskMasterId !== this.taskMasterId) return false;
    if (item.taskType !== this.taskType) return false;
    if (item.taskTypeId !== this.taskTypeId) return false;
    if (item.taskTypeFamilyId !== this.taskTypeFamilyId) return false;
    if (item.auditTrailId !== this.auditTrailId) return false;
    if (item.commentTrailId !== this.commentTrailId) return false;
    if (item.checkList.isEqual(this.checkList) === false) return false;
    if (getDateTimeDiff(item.startDateTime, this.startDateTime)) return false;
    if (getDateTimeDiff(item.endDateTime, this.endDateTime)) return false;
    if (getDateTimeDiff(item.deadline, this.deadline)) return false;
    if (item.duration !== this.duration) return false;
    if (item.hasFollowUp !== this.hasFollowUp) return false;
    if (item.userId !== this.userId) return false;
    if (item.recurringPattern.isEqual(this.recurringPattern) === false) return false;
    if (item.eventId !== this.eventId) return false;
    if (getDateTimeDiff(item.created, this.created)) return false;
    if (getDateTimeDiff(item.completed, this.completed)) return false;
    if (item.creatorId !== this.creatorId) return false;
    if (item.ownerId !== this.ownerId) return false;
    if (item.instancesEnabled !== this.instancesEnabled) return false;
    if (item.hidden !== this.hidden) return false;
    if (item.approved !== this.approved) return false;
    if (item.authSchemaId !== this.authSchemaId) return false;

    // 1. do not compare the completed property: this can only be changed by an admin and is saved directly
    // this would lead to a change here that is already saved.
    // 2. do not compare resource links because they are saved directly in the TaskLinkList

    if (
      areDifferent(item.tagIds, this.tagIds, (a: number, b: number) => {
        return a === b;
      }) === true
    ) {
      return false;
    }

    if (
      areDifferent(item.resourceLinkIds, this.resourceLinkIds, (a: number, b: number) => {
        return a === b;
      }) === true
    ) {
      return false;
    }

    if (
      areDifferent(item.controlIds, this.controlIds, (a: number, b: number) => {
        return a === b;
      }) === true
    ) {
      return false;
    }

    if (
      areDifferent(item.objectiveIds, this.objectiveIds, (a: number, b: number) => {
        return a === b;
      }) === true
    ) {
      return false;
    }

    if (
      areDifferent(item.processIds, this.processIds, (a: number, b: number) => {
        return a === b;
      }) === true
    ) {
      return false;
    }

    if (
      areDifferent(item.riskIds, this.riskIds, (a: number, b: number) => {
        return a === b;
      }) === true
    ) {
      return false;
    }

    if (
      areDifferent(item.assetIds, this.assetIds, (a: number, b: number) => {
        return a === b;
      }) === true
    ) {
      return false;
    }

    if (
      areDifferent(item.normIds, this.normIds, (a: number, b: number) => {
        return a === b;
      }) === true
    ) {
      return false;
    }

    if (areTaskRelationsDifferent(item.taskRelations, this.taskRelations)) {
      return false;
    }

    if (
      areDifferent(item.kpiData, this.kpiData, (a: KPIData, b: KPIData) => {
        return a.isEqual(b);
      }) === true
    ) {
      return false;
    }

    if (
      areDifferent(item.instances, this.instances, (a: Task, b: Task) => {
        return a.isEqual(b);
      }) === true
    ) {
      return false;
    }

    if (
      areDifferent(item.processingActivityIds, this.processingActivityIds, (a: number, b: number) => {
        return a === b;
      }) === true
    ) {
      return false;
    }

    return true;
  }

  updateWillRecreateInstances(oldTask: Task) {
    //date changes will, time changes not
    const oldTaskStartDate = new Date(oldTask.startDateTime);
    oldTaskStartDate.setHours(0, 0, 0, 0);
    const newTaskStartDate = new Date(this.startDateTime);
    newTaskStartDate.setHours(0, 0, 0, 0);

    if (getDateTimeDiffDays(oldTaskStartDate, newTaskStartDate) !== 0) {
      return true;
    }

    //recurrence pattern changes will, the start date lock flag does not
    if (oldTask.recurringPattern.isEqual(this.recurringPattern, true) === false) {
      return true;
    }

    return false;
  }

  // Validate function that validates the contents of the fields that have user input and can be written to the database
  // - Set abortEarly=false to make sure all errors are returned for the class
  // - Use getLocalizedMessageOptions() from the Localization service to get localized error messages
  // - The localizedFields array must be used to give each field in the error message a localized label
  validate(localizedFields: Record<string, string>): Joi.ValidationResult {
    const schema = Joi.object({
      assignment: Joi.string().guid().optional().label(localizedFields['assignment']),
      startDateTime: Joi.date().required().label(localizedFields['startDateTime']),
      endDateTime: Joi.date().required().min(Joi.ref('startDateTime')).label(localizedFields['endDateTime']),
      deadline: Joi.date().min(Joi.ref('endDateTime')).label(localizedFields['deadline']),
      duration: Joi.number().required().min(0),
      taskStateId: Joi.number().required(),
      name: Joi.string().required().max(512).label(localizedFields['name']),
      externalId: Joi.string().allow('').max(256),
      //form answers travel with every save, a draft included: one value over the API limit fails the whole task
      kpiData: Joi.array().items(
        Joi.object({
          resultText: Joi.string().allow('', null).max(KPIData.textMaxLength),
          resultComment: Joi.string().allow('', null).max(KPIData.commentMaxLength),
        }),
      ),
    }).prefs(getLocalizedMessageOptions());

    const objToValidate = {
      assignment: this.userId || undefined,
      startDateTime: this.startDateTime,
      endDateTime: this.endDateTime,
      deadline: this.taskType === TaskTypes.Normal && !this.isNonRecurring() ? undefined : this.deadline,
      duration: this.duration,
      taskStateId: this.taskStateId,
      name: this.name,
      externalId: this.externalId?.trim() ?? '',
      kpiData: this.kpiData?.map((data) => ({ resultText: data.resultText, resultComment: data.resultComment })) ?? [],
    };

    return schema.validate(objToValidate, { abortEarly: false });
  }

  addEntities(entities: Entity[] | undefined) {
    if (!entities) return;
    for (let idx = 0; idx < entities.length; idx++) {
      const entity = entities[idx];
      this.addEntity(entity);
    }
  }

  addEntity(entity: Entity) {
    if (!entity.isDeleted) {
      switch (entity.typeOfEntity) {
        case EntityTypes.Process: {
          if (!this.processIds.includes(entity.entityId)) this.processIds.push(entity.entityId);
          break;
        }
        case EntityTypes.Requirement:
        case EntityTypes.Control: {
          if (!this.controlIds.includes(entity.entityId)) this.controlIds.push(entity.entityId);
          break;
        }
        case EntityTypes.Objective: {
          if (!this.objectiveIds.includes(entity.entityId)) this.objectiveIds.push(entity.entityId);
          break;
        }
        case EntityTypes.Risk: {
          if (!this.riskIds.includes(entity.entityId)) this.riskIds.push(entity.entityId);
          break;
        }
        case EntityTypes.Asset: {
          if (!this.assetIds.includes(entity.entityId)) this.assetIds.push(entity.entityId);
          break;
        }
        case EntityTypes.ProcessingActivity: {
          if (!this.processingActivityIds.includes(entity.entityId)) this.processingActivityIds.push(entity.entityId);
          break;
        }
        // An internal audit is deliberately absent: a task is never linked to an audit as context.
        // The audit's own execution work is linked by the audit itself, and a finding is not linked
        // at all - it is a task following up an execution task (see apiAddTaskRelation).
      }
    }
  }

  applyTemplate(template: Task, appContext: IAppContext): Task {
    try {
      //some prechecks
      if (template.taskType !== TaskTypes.Template && template.taskType !== TaskTypes.EventTemplate) {
        throw new AppError('Cannot apply a task that is not a template');
      }

      //start with cloning the complete task
      const output = this.clone();
      output.taskStates = appContext.globalDataCache.taskStates.items;

      //set the task id of the template on the created task
      output.templateId = template.taskId;

      //set the owner
      output.ownerId = template.ownerId;
      output.owner = template.owner;

      //set owner to current user when template is owner by System
      if (output.ownerId === newGuidNil()) {
        output.ownerId = appContext.user.id;
        output.owner = appContext.user.clone();
      }

      //set the permissinos
      output.authSchemaId = template.authSchemaId;

      //apply each property in the template
      output.name = template.name;
      output.description = template.description;
      output.hasFollowUp = false;
      output.userId = template.userId;
      output.checkList = template.checkList.clone();
      //the form binding travels complete, and through the family: a template read before its form was
      //forked still names the version it left, while the server has long repointed the template to the
      //head. A new task always gets the head, which is the form the template produces today
      const form = appContext.globalDataCache.taskTypes.getHead(template.taskTypeId, template.taskTypeFamilyId);
      const newTaskTypeId = form?.taskTypeId ?? template.taskTypeId;
      if (newTaskTypeId !== output.taskTypeId) {
        //the rows belong to the form the task had (a previous template's defaults, whatever was typed on
        //it): another form, or none, takes none of them (see assignForm)
        output.kpiData = undefined;
      }
      output.taskTypeId = newTaskTypeId;
      output.taskTypeFamilyId = form?.taskTypeFamilyId ?? template.taskTypeFamilyId;
      output.formVersion = (form ?? template.formVersion)?.clone();
      //the screen follows the form: a template with a DPIA form opens the DPIA screen right away
      output.setSystemTaskType(output.formVersion?.systemTaskType ?? SystemTaskTypes.None);
      //a new task exists only in the browser, so it inherits the template's kind; the server stamps it on save.
      //A saved task keeps its own kind
      if (output.taskId === -1) output.taskKind = template.taskKind;
      output.instancesEnabled = template.instancesEnabled;

      //note that the recurrence pattern is not copied because it is not allowed in a template and
      //it should never overwrite the recurrence pattern of an annual plan task

      //add the related entities of the template, keeping the entities that are already on the task
      //(a task created from an entity, such as a risk analysis task, is pre-linked to that entity)
      output.controlIds = addIds(output.controlIds, template.controlIds);
      output.objectiveIds = addIds(output.objectiveIds, template.objectiveIds);
      output.processIds = addIds(output.processIds, template.processIds);
      output.riskIds = addIds(output.riskIds, template.riskIds);
      output.assetIds = addIds(output.assetIds, template.assetIds);
      output.processingActivityIds = addIds(output.processingActivityIds, template.processingActivityIds);

      //add tags
      if (template.tagIds) {
        output.tagIds = [];
        for (const tagId of template.tagIds) {
          if (!output.tagIds.includes(tagId)) {
            output.tagIds.push(tagId);
          }
        }
      }

      //add links
      if (template.resourceLinkIds) {
        output.resourceLinkIds = [];
        for (const linkId of template.resourceLinkIds) {
          if (!output.resourceLinkIds.includes(linkId)) {
            output.resourceLinkIds.push(linkId);
          }
        }
      }

      if (this.taskType === TaskTypes.Monitoring) {
        //when this is a Monitoring, don't set the task type to template because a monitoring task is a sort of template itself
        //and keep the recurrence pattern because its mandatory for monitoring tasks
      } else if (this.taskType === TaskTypes.Event && this.taskMasterId !== 0) {
        //when this is a subtask of an event, do not change the type
      } else {
        if (template.taskType === TaskTypes.EventTemplate) {
          output.taskType = TaskTypes.Event;
          output.taskMasterId = 0;
          //when creating an event form a template, the created event is a series
          //that can contain instances so set taskMasterId to 0
        } else if (template.taskType === TaskTypes.Template) {
          output.taskType = TaskTypes.Normal;
          output.taskMasterId = undefined;
          output.instances = [];
          //when creating a task form a template, the created task does not start as a series
          //the temmplate cannot contain a recurrence pattern so set taskMasterId to undefined
        }
      }

      //set instances from the event template
      if (template.taskType === TaskTypes.EventTemplate && output.taskType === TaskTypes.Event) {
        const templateInstances = template.instances ?? [];
        output.instances = [];

        for (let idx = 0; idx < templateInstances.length; idx++) {
          const templateInstance = templateInstances[idx];
          const instance = new Task().applyTemplate(templateInstance, appContext);
          instance.id = newGuid();
          instance.taskMasterId = output.taskId;
          instance.taskStateId = templateInstance.taskStateId;
          output.instances.push(instance);
        }
      }

      //links are not applied here

      return output;
    } catch (err) {
      appContext.setError(err);

      return this;
    }
  }
}
